import React, { useMemo } from 'react';
import { styles, StyledRightContentWrapper, StyledProgressSummaryContentWrapper } from '@apps/registry/common/components/RegistryGiftCounts/RegistryGiftCounts.styles';
import { useTranslation } from '@shared/core';
import { Flex } from '@withjoy/joykit';
import ClampedText from '@apps/registry/common/components/ClampedText';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { SkeletonPriceRanges, SkeletonProgressSummary, SkeletonRegistryGuideTitle } from '@apps/registry/common/components/RegistryGiftCounts/components/Skeletons';
import { Page } from '@apps/registry/common/components/RegistryGiftCounts/telemetry';
import { useRegistryGiftCounts } from '@apps/registry/common/components/RegistryGiftCounts/RegistryGiftCounts.controller';
import { LoadingState, EditState, Tooltip, CountsByPrice } from '@apps/registry/common/components/RegistryGiftCounts/components';
import { ProgressSummary } from '../../ProgressSummary';

export interface RegistryGiftCountsProps {
  eventId: string;
  page: Page;
  eventHandle: string;
  onActionComplete?: () => void;
}

interface RegistryGiftCountExpandableProps extends RegistryGiftCountsProps {
  isOpenByDefault?: boolean;
}

export const RegistryGiftCountsDetails = ({ eventId, page, eventHandle, onActionComplete }: RegistryGiftCountExpandableProps) => {
  const {
    getResultsLoading,
    initialDataLoading,
    formik,
    handleOnEditClick,
    isEditing,
    getPriceRangeTitleAndLink,
    completionPercent,
    subtitle,
    formattedCompletionPercent,
    priceRanges,
    targetGiftCount,
    currentGiftCount,
    isInZeroState
  } = useRegistryGiftCounts({ eventId, page, eventHandle });

  const { t } = useTranslation('sharedComponents');
  const { registryGuide } = t('registryGiftCounts');

  const giftCountsStateContent = useMemo(() => {
    if (getResultsLoading) {
      return (
        <Flex width="100%" height="520px">
          <LoadingState />
        </Flex>
      );
    } else if (!initialDataLoading && (isEditing || isInZeroState)) {
      return (
        <Flex width="100%" height="520px">
          <EditState formik={formik} />
        </Flex>
      );
    }

    return (
      <>
        <Flex justifyContent="space-between" width="100%" alignItems="center" marginBottom={4}>
          <SkeletonGroup width="100%" isReady={!initialDataLoading} placeholder={<SkeletonRegistryGuideTitle />}>
            <Flex alignItems="center" justifyContent="center">
              <ClampedText typographyVariant="hed2" marginRight={2} clampAt={1}>
                {registryGuide()}
              </ClampedText>
              <Tooltip />
            </Flex>
          </SkeletonGroup>
        </Flex>

        <StyledProgressSummaryContentWrapper __css={styles.progressSummaryContentWrapper}>
          <SkeletonGroup width="100%" isReady={!initialDataLoading} placeholder={<SkeletonProgressSummary />}>
            <ProgressSummary
              completionPercent={completionPercent}
              subtitle={subtitle}
              formattedCompletionPercent={formattedCompletionPercent}
              handleOnEditClick={handleOnEditClick}
              targetGiftCount={targetGiftCount}
              currentGiftCount={currentGiftCount}
            />
          </SkeletonGroup>
        </StyledProgressSummaryContentWrapper>

        <StyledRightContentWrapper __css={styles.rightContentWrapper} opacity={isEditing || getResultsLoading || isInZeroState ? 0.5 : 1}>
          <SkeletonGroup isReady={!initialDataLoading} placeholder={<SkeletonPriceRanges wrapItems />}>
            <CountsByPrice isOpen getPriceRangeTitleAndLink={getPriceRangeTitleAndLink} priceRanges={priceRanges} page={page} onActionComplete={onActionComplete} wrapItems />
          </SkeletonGroup>
        </StyledRightContentWrapper>
      </>
    );
  }, [
    getResultsLoading,
    isEditing,
    isInZeroState,
    initialDataLoading,
    registryGuide,
    completionPercent,
    subtitle,
    formattedCompletionPercent,
    handleOnEditClick,
    targetGiftCount,
    currentGiftCount,
    getPriceRangeTitleAndLink,
    priceRanges,
    page,
    formik,
    onActionComplete
  ]);

  return <>{giftCountsStateContent}</>;
};
